/**
 * SectionCommentThread - Collapsible section with comment thread
 */
import {
  CheckCircleIcon,
  CpuChipIcon,
  ExclamationTriangleIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import React, { useState } from 'react';

import { Comment } from '../../types/review';
import { MonacoMarkdownEditor } from '../monaco/MonacoMarkdownEditor';

import { CommentItem } from './CommentItem';

interface SectionCommentThreadProps {
  sectionId: string;
  sectionTitle: string;
  sectionContent: string;
  comments: Comment[];
  isReviewer: boolean;
  canReply: boolean;
  isOwner?: boolean;
  canAddComment: boolean;
  currentReviewerId?: string | null;
  onAddComment: (commentText: string) => Promise<void>;
  onEditComment: (commentId: string, newText: string) => Promise<void>;
  onDeleteComment: (commentId: string) => Promise<void>;
  onReplyComment: (parentId: string, replyText: string) => Promise<void>;
  onToggleResolve: (commentId: string, currentStatus: string) => Promise<void>;
  currentUserEmail: string;
  onInteract?: (sectionId: string, sectionTitle: string) => void;
  hideResolveButton?: boolean;
  /** Rendered at the top of the comment list (e.g. an Archie fix card shown
   *  as the first comment in the thread). */
  pinnedContent?: React.ReactNode;
  /** One-line descriptions for each Archie addition in the section content,
   *  shown after the "Added by Archie" label on each highlighted change. */
  archieChangeDescriptions?: string[];
  /** Namespaces the rendered heading ids so the Peer Review Comments
   *  table-of-contents can scroll to individual sub-headings in this section. */
  headingIdPrefix?: string;
}

export const SectionCommentThread: React.FC<SectionCommentThreadProps> = ({
  sectionId,
  sectionTitle,
  sectionContent,
  comments,
  isReviewer,
  isOwner = false,
  canReply,
  canAddComment,
  currentReviewerId,
  onAddComment,
  onEditComment,
  onDeleteComment,
  onReplyComment,
  onToggleResolve,
  onInteract,
  hideResolveButton = false,
  pinnedContent,
  archieChangeDescriptions,
  headingIdPrefix,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInteraction = () => {
    if (onInteract) {
      onInteract(sectionId, sectionTitle);
    }
  };

  // Build tree structure from flat comment list
  const rootComments = React.useMemo(() => {
    const commentMap = new Map<string, Comment>();
    const roots: Comment[] = [];

    // Initialize map with comments and empty replies array
    comments.forEach(c => {
      commentMap.set(c.id, { ...c, replies: [] });
    });

    // Build tree
    comments.forEach(c => {
      const node = commentMap.get(c.id)!;
      if (c.parent_id && commentMap.has(c.parent_id)) {
        commentMap.get(c.parent_id)!.replies!.push(node);
      } else {
        roots.push(node);
      }
    });
    
    // Sort roots by date
    return roots.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  }, [comments]);

  // Header count badges are computed from top-level comments only (replies
  // don't get their own count). Each status/type gets a distinct colour so the
  // collapsed header tells the reviewer, at a glance, what's outstanding.
  const openComments = comments.filter(
    (c) => c.status === 'open' && !c.parent_id,
  );
  const resolvedComments = comments.filter(
    (c) => c.status === 'resolved' && !c.parent_id,
  );
  const requestedChangeComments = comments.filter(
    (c) => c.comment_type === 'request_changes' && !c.parent_id,
  );
  // Archie's contributions arrive as highlighted section edits (not Comment
  // rows), so its count is the number of applied changes in this section.
  const archieChangeCount = archieChangeDescriptions?.length ?? 0;

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || isSubmitting) {return;}
    
    handleInteraction(); // Update context when adding comment

    setIsSubmitting(true);
    try {
      await onAddComment(newComment);
      setNewComment('');
      setIsAddingComment(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="border border-base-300 rounded-lg bg-base-100">
      {/* Section Header */}
      <button
        type="button"
        className="w-full p-4 cursor-pointer hover:bg-base-200 flex items-center justify-between text-left border-0"
        onClick={() => {
          setIsExpanded(!isExpanded);
          handleInteraction();
        }}
        aria-expanded={isExpanded}
      >
        <div className="flex items-center gap-2">
          <span className="text-xl">{isExpanded ? '▼' : '▶'}</span>
          <span className="text-lg font-medium">{sectionTitle}</span>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-1.5">
          {openComments.length > 0 && (
            <span className="badge badge-warning gap-1">
              <PencilIcon className="h-3.5 w-3.5" />
              {openComments.length} open
            </span>
          )}
          {resolvedComments.length > 0 && (
            <span className="badge badge-success gap-1">
              <CheckCircleIcon className="h-3.5 w-3.5" />
              {resolvedComments.length} resolved
            </span>
          )}
          {requestedChangeComments.length > 0 && (
            <span className="badge badge-error gap-1">
              <ExclamationTriangleIcon className="h-3.5 w-3.5" />
              {requestedChangeComments.length}{' '}
              {requestedChangeComments.length === 1 ? 'change' : 'changes'}{' '}
              requested
            </span>
          )}
          {archieChangeCount > 0 && (
            <span className="badge gap-1 border border-emerald-300 bg-emerald-100 text-emerald-700">
              <CpuChipIcon className="h-3.5 w-3.5" />
              {archieChangeCount} Archie
            </span>
          )}
        </div>
      </button>

      {/* Collapsible Content */}
      {isExpanded && (
        <div className="p-4 border-t border-base-300 bg-gray-50">
          {/* Monaco Markdown Preview - Only show if content exists */}
          {sectionContent && (
            <div className="mb-6 border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
              <MonacoMarkdownEditor
                value={sectionContent}
                onChange={() => {/* read-only */}}
                height="400px"
                theme="light"
                previewOnly={true}
                archieChangeDescriptions={archieChangeDescriptions}
                headingIdPrefix={headingIdPrefix}
              />
            </div>
          )}

          <div className="space-y-3">
            {pinnedContent}

            {comments.length === 0 && !isAddingComment && !pinnedContent && (
              <div className="text-center py-6 text-base-content/60">
              No comments yet
              </div>
            )}

            {rootComments.map((comment, index) => (
              <div
                key={comment.id}
                style={{ animation: 'fadeIn 0.3s ease-in forwards', animationDelay: `${index * 50}ms` }}
              >
                <CommentItem
                  comment={comment}
                  isReviewer={isReviewer}
                  isOwner={isOwner}
                  canReply={canReply}
                  currentReviewerId={currentReviewerId}
                  hideResolveButton={hideResolveButton}
                  onEditComment={onEditComment}
                  onDeleteComment={onDeleteComment}
                  onReplyComment={onReplyComment}
                  onToggleResolve={onToggleResolve}
                />
              </div>
            ))}

            {canAddComment && (
              <div>
                {!isAddingComment ? (
                  <button
                    className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border-2 border-dashed border-gray-300 hover:border-[#008557] hover:text-[#008557] rounded-md transition-colors"
                    onClick={() => setIsAddingComment(true)}
                  >
                    + Add Comment
                  </button>
                ) : (
                  <form onSubmit={handleAddComment} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <textarea
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-[#008557] focus:ring-2 focus:ring-[#008557]/20 focus:outline-none resize-none"
                      placeholder="Add your comment..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      rows={3}
                    />
                    <div className="flex gap-2 justify-end mt-3">
                      <button
                        type="button"
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md transition-colors"
                        onClick={() => {
                          setIsAddingComment(false);
                          setNewComment('');
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 text-sm font-medium text-white bg-[#008557] hover:bg-[#006b45] rounded-md transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
                        disabled={!newComment.trim() || isSubmitting}
                      >
                        {isSubmitting && (
                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        )}
                        {isSubmitting ? 'Posting...' : 'Post Comment'}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
